import { Input, InputProps } from 'antd';
import React, { useCallback } from 'react';

interface NumberInputProps extends Omit<InputProps, 'onChange' | 'value' | 'onBlur'> {
  value?: string | number;
  onChange?: (value: number | string | undefined) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  min?: number;
  max?: number;
  returnNumber?: boolean; // If true, returns number, otherwise returns string
}

/**
 * Normalizes comma to dot (comma is decimal separator)
 */
const normalize = (value: string): string => value.replace(/,/g, '.');

/**
 * Checks if value is an intermediate state that should be preserved as string
 */
const isIntermediate = (value: string): boolean => {
  return value === '' || value === '.' || /^\d+\.$/.test(value);
};

/**
 * Number input component that only accepts numbers, comma, and dot
 * Prevents entering a second comma or dot
 * Automatically replaces comma with dot (comma is decimal separator)
 * Applies min/max constraints on blur
 */
const NumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  onBlur,
  min,
  max,
  returnNumber = false,
  ...props
}) => {
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty string
    if (inputValue === '') {
      onChange?.(returnNumber ? undefined : '');
      return;
    }

    // Only allow numbers, comma, and dot
    const allowedPattern = /^[0-9,.]*$/;
    if (!allowedPattern.test(inputValue)) {
      return; // Ignore invalid characters
    }

    // Normalize comma to dot for checking
    const normalizedForCheck = normalize(inputValue);
    const dotCount = (normalizedForCheck.match(/\./g) || []).length;

    // Prevent adding a second dot (after comma replacement)
    if (dotCount > 1) {
      return; // Prevent adding second dot (or second comma which becomes dot)
    }

    // Normalize the value
    const normalized = normalize(inputValue);

    // Check if this is an intermediate state
    const intermediate = isIntermediate(normalized);

    if (returnNumber) {
      if (intermediate) {
        // Preserve intermediate states as string (e.g., "0." or "1.")
        onChange?.(normalized);
      } else {
        // Parse to number
        const numValue = parseFloat(normalized);
        if (!isNaN(numValue)) {
          // Don't apply min/max here - wait for blur
          onChange?.(numValue);
        } else {
          // Invalid state, preserve as string
          onChange?.(normalized);
        }
      }
    } else {
      // Return string value (for filters)
      onChange?.(normalized);
    }
  }, [onChange, returnNumber]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (returnNumber && inputValue !== '') {
      const normalized = normalize(inputValue);

      // Finalize intermediate states on blur
      if (isIntermediate(normalized)) {
        // "1." → 1, "." → undefined
        if (normalized === '.') {
          onChange?.(undefined);
        } else {
          // "1." → parse to 1
          const numValue = parseFloat(normalized);
          if (!isNaN(numValue)) {
            let finalValue = numValue;
            if (min !== undefined && finalValue < min) finalValue = min;
            if (max !== undefined && finalValue > max) finalValue = max;
            onChange?.(finalValue);
          } else {
            onChange?.(undefined);
          }
        }
        onBlur?.(e);
        return;
      }

      // Apply min/max constraints on blur for non-intermediate values
      const numValue = parseFloat(normalized);
      if (!isNaN(numValue)) {
        let finalValue = numValue;
        if (min !== undefined && finalValue < min) finalValue = min;
        if (max !== undefined && finalValue > max) finalValue = max;

        // Only update if value changed
        if (finalValue !== numValue) {
          onChange?.(finalValue);
        }
      }
    }

    onBlur?.(e);
  }, [onChange, min, max, returnNumber, onBlur]);

  // Convert number value to string for display
  const displayValue = value !== null && value !== undefined ? String(value) : '';

  return (
    <Input
      {...props}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export default NumberInput;
